import { CSSProperties, FC, useMemo } from 'react'

import { Box, Flex, SimpleGrid, Stack } from '@mantine/core'
import { useIntl } from 'react-intl'

import { getHelloSitesUrl } from 'api/utils/paths'
import FeatureCard from 'components/molecules/FeatureCard'
import { FeatureCardLayout } from 'components/molecules/FeatureCard/types'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { basePath } from 'interfaces/constants'
import { ExpertUserSummaryApi } from 'interfaces/types'

import { ExpertItem } from './ExpertItem'
import { useHomeExpert } from './HomeExpert.hooks'
import CarouselCard from '../CarouselCardV2/'
import GroupItems from '../groupItems'

export type HomeExpertProps = {
  data?: { title: string }
  experts?: ExpertUserSummaryApi[]
  className?: string
  style?: CSSProperties
  maxDisplayMobile?: number
}
export const HomeExpert: FC<HomeExpertProps> = (props) => {
  const { data, experts: expertsProps = [], maxDisplayMobile = 15 } = props
  const { formatMessage: f } = useIntl()
  const { experts = [] } = useHomeExpert({ experts: expertsProps })
  const { locale } = useRouterExtend()
  const isMobile = useIsMobile()
  const displayList = useMemo(
    () => (isMobile ? experts.slice(0, maxDisplayMobile) : experts),
    [experts, isMobile],
  )
  const currentDomain = useMemo(() => getHelloSitesUrl(locale), [locale])
  if (displayList.length === 0) return null
  return (
    <FeatureCard
      color="gray"
      layout={FeatureCardLayout.VerticalCenter}
      url="/expert/"
    >
      <FeatureCard.SectionName
        icon="/images/expert-icon.png"
        data-event-category="Recommend Experts"
        data-event-action="See All Click"
        data-event-label={`${currentDomain}${basePath}/expert`}
      >
        {f({
          id: 'home.expert.iconTitle',
          defaultMessage: 'EXPERT PANEL',
        })}
      </FeatureCard.SectionName>
      <FeatureCard.Heading>{data?.title}</FeatureCard.Heading>
      <FeatureCard.Content>
        <FeatureCard.Inner
          sx={(theme) => ({
            [theme.fn.smallerThan('sm')]: {
              padding: '0 16px',
            },
            [theme.fn.largerThan('sm')]: {
              paddingBottom: 40,
            },
          })}
        >
          <CarouselCard
            align={'start'}
            slideSize={'100%'}
            slideGap={16}
            slidesToScroll={1}
          >
            <Stack spacing={24}>
              <CarouselCard.Body>
                <GroupItems
                  ItemWrapper={(children) => (
                    <Box
                      sx={(theme) => ({
                        padding: '2px 1px',
                        [theme.fn.largerThan('sm')]: {
                          ...(displayList.length < 5 && {
                            width: '20%',
                          }),
                        },
                      })}
                    >
                      {children}
                    </Box>
                  )}
                  GroupWrapper={(children) => (
                    <CarouselCard.Slide>
                      {displayList.length < 5 ? (
                        <Flex gap={12} align={'center'} justify={'center'}>
                          {children}
                        </Flex>
                      ) : (
                        <SimpleGrid cols={5}>{children}</SimpleGrid>
                      )}
                    </CarouselCard.Slide>
                  )}
                  breakpoints={{
                    base: {
                      itemsPerGroup: displayList.length,
                      GroupWrapper: (children) => (
                        <CarouselCard.Slide>
                          <Stack spacing={12}>{children}</Stack>
                        </CarouselCard.Slide>
                      ),
                      ItemWrapper: (children) => (
                        <Box
                          sx={{
                            padding: 1,
                          }}
                        >
                          {children}
                        </Box>
                      ),
                    },
                    md: {
                      itemsPerGroup: 10,
                      GroupWrapper: (children) => (
                        <CarouselCard.Slide>
                          {displayList.length < 5 ? (
                            <Flex gap={12} align={'center'} justify={'center'}>
                              {children}
                            </Flex>
                          ) : (
                            <SimpleGrid cols={5}>{children}</SimpleGrid>
                          )}
                        </CarouselCard.Slide>
                      ),
                    },
                  }}
                  items={displayList.map((item, index) => (
                    <ExpertItem
                      key={String(index)}
                      title={item.display_name}
                      titleHref={item.anchorProps?.href}
                      subTitle={item.organisation}
                      subTitleHref={item.organisation_url}
                      avatar={item.avatar}
                      role={item.role}
                      sx={{
                        height: '100%',
                      }}
                    />
                  ))}
                />
              </CarouselCard.Body>
              <CarouselCard.Footer
                sx={{
                  ...(displayList.length <= 10 && {
                    justifyContent: 'center',
                  }),
                }}
              >
                <FeatureCard.ViewAll
                  className="homeExpertButton"
                  data-event-category="Recommend Experts"
                  data-event-action="See All Click"
                  data-event-label={`${currentDomain}${basePath}/expert`}
                  fullWidth={false}
                >
                  {f({
                    id: 'home.expert.buttonViewAll',
                    defaultMessage: 'View all Experts',
                  })}
                </FeatureCard.ViewAll>
                <CarouselCard.Controls>
                  <CarouselCard.Control
                    direction="prev"
                    data-event-category="Recommend Category"
                    data-event-action=" See More Article"
                    data-event-label={`${currentDomain}${basePath}`}
                  />
                  <CarouselCard.Control
                    direction="next"
                    data-event-category={'Recommend Category'}
                    data-event-action="See More Article"
                    data-event-label={`${currentDomain}${basePath}`}
                  />
                </CarouselCard.Controls>
              </CarouselCard.Footer>
            </Stack>
          </CarouselCard>
        </FeatureCard.Inner>
      </FeatureCard.Content>
    </FeatureCard>
  )
}
